import { useTransition, useCallback, useState, Fragment, useRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { Loader2, UserPlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { debounce } from 'lodash';

import { UserColumnsEnum, QueryOrderEnum } from 'src/graphql/schema';
import { IntegrationMemberCreateDocument } from 'src/graphql/actions/integration/integrationMemberCreate.mutation';
import { IntegrationMembersDocument } from 'src/graphql/actions/integration/integrationMembers.query';
import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTrigger,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { ClientError, getApiError } from 'src/utils/errors';
import { withSuspense } from 'src/hoc';

import { refetchIntegrationMembers } from '../api';

const schema = z.object({
  userId: z
    .string({
      message: 'Select a user to share access with',
    })
    .min(1, 'Select a user to share access with'),
});

type IFormValues = z.infer<typeof schema>;

type IContainerProps = {
  integrationId: string;
  setOpen: (next: boolean) => void;
  ref: React.RefObject<{
    reset: () => void;
  } | null>;
};

const Container = withSuspense(({ integrationId, setOpen, ref }: IContainerProps) => {
  const [query, setQuery] = useState<string | undefined>();
  const [inputValue, setInputValue] = useState('');
  const [isPending, startTransition] = useTransition();

  const isSearchPending = isPending || inputValue !== (query ?? '');

  const form = useForm<IFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { userId: '' },
  });

  useImperativeHandle(
    ref,
    () => ({
      reset: form.reset,
    }),
    [],
  );

  const {
    data: { users },
  } = useSuspenseQuery(UsersDocument, {
    variables: { orderBy: UserColumnsEnum.Name, order: QueryOrderEnum.Asc, ...(query && { queryFilter: query }) },
  });
  const {
    data: { integrationMembers },
  } = useSuspenseQuery(IntegrationMembersDocument, {
    variables: {
      integrationId,
    },
  });

  const existingMemberIds = integrationMembers.map(({ user }) => user.id) || [];
  const [createMember, { loading }] = useMutation(IntegrationMemberCreateDocument, {
    onCompleted: refetchIntegrationMembers,
  });

  const userOptions = users.nodes
    .filter((u) => !existingMemberIds.includes(u.id))
    .map((u) => ({ value: u.id, label: u.name ?? '' }));

  const onSubmit = async ({ userId }: IFormValues) => {
    try {
      const { data } = await createMember({ variables: { integrationId, userId } });
      if (data?.integrationMemberCreate?.success) {
        setOpen(false);
        form.reset();
        toast.success('Access granted');
      } else {
        throw new ClientError(data?.integrationMemberCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const debouncedSetQuery = useCallback(
    debounce((value: string) => {
      startTransition(() => setQuery(value));
    }, 500),
    [startTransition],
  );

  const onInputValueChange = useCallback(
    (value: string, { reason }: { reason: string }) => {
      if (reason === 'item-press') return;

      setInputValue(value);
      debouncedSetQuery(value);
    },
    [debouncedSetQuery],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <Combobox
                items={userOptions}
                itemToStringValue={(user) => user?.label ?? ''}
                onValueChange={(user: { value: string; label: string } | null) => field.onChange(user?.value)}
                onInputValueChange={onInputValueChange}
              >
                <FormControl>
                  <ComboboxInput placeholder="Search for a user" showClear />
                </FormControl>
                <ComboboxContent>
                  {isSearchPending ? (
                    <div className="flex justify-center p-2">
                      <Loader2 className="size-4 animate-spin" />
                    </div>
                  ) : (
                    <Fragment>
                      <ComboboxList>
                        {(item) => (
                          <ComboboxItem key={item.value} value={item}>
                            {item.label}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                      <ComboboxEmpty>No users found</ComboboxEmpty>
                    </Fragment>
                  )}
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          )}
        />
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
          <Button type="submit" size="sm" disabled={loading} isLoading={loading}>
            Share access
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
});

type Props = {
  integrationId: string;
  defaultOpen?: boolean;
  onClose?: () => void;
};

const ShareAccessDialog = ({ integrationId, defaultOpen = false, onClose }: Props) => {
  const [open, setOpen] = useState(defaultOpen);
  const formRef = useRef<{
    reset: () => void;
  }>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      formRef?.current?.reset();
      onClose?.();
    }
    setOpen(next);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button size="sm" className="w-full">
          <UserPlusIcon /> Share access
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Share access to this integration</AlertDialogTitle>
          <AlertDialogDescription>
            Team members with access can use this integration to add assets to their products.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Container integrationId={integrationId} setOpen={setOpen} ref={formRef} />
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ShareAccessDialog;
